import { forwardRef, useMemo } from 'react'
import { Country } from 'react-phone-number-input'

import useDebounceValue from 'hooks/use-debounce-value'
import useAppSetting from 'hooks/use-app-setting'
import { usePlaceData } from './hooks'

import { Flex, Icon, Toast, Tooltip } from 'components'
import SynchronousAutocomplete from 'components/Dropdown/SynchronousAutocomplete'
import Text from 'components/Text'
import { AsyncDropdownProps, DropdownItem } from 'components/Dropdown/types'

import { BaseAddress } from 'types/global'
import { CustomAddressConfiguration } from 'store/types/settings'
import { Prediction } from 'services/place-search'

import { createAddress } from './helper'
import { colors } from 'utils/colors'

export type SearchResult = {
  address: BaseAddress
  rawPlace?: Prediction
}

type Option = BaseAddress & {
  addressId: string
}

type Value = BaseAddress & {
  addressId?: string
}

type Props = Omit<AsyncDropdownProps, 'defaultOptions' | 'value'> & {
  countryFallback: Country
  onSelectResult: (value: SearchResult) => void
  otherOptions?: Option[]
  showIcon?: boolean
  value?: Value
  autoSuggestion?: boolean
  placeholder?: string
  addressConfigs: CustomAddressConfiguration[]
  placeInfoIconRight?: boolean
}

const SearchPlace = forwardRef<HTMLInputElement, Props>(
  (
    {
      countryFallback,
      onSelectResult,
      otherOptions,
      value,
      autoSuggestion = true,
      addressConfigs,
      placeholder = 'Find address',
      isCreatable = true,
      placeInfoIconRight = false,
      cacheOptions = true,
      ...props
    },
    ref,
  ) => {
    const [input, setInput] = useDebounceValue('', 1000)

    const searchSessionToken = useMemo(() => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, [])
    const defaultOptions: DropdownItem<string>[] = useMemo(
      () => otherOptions?.map((i) => ({ label: i.formatted ?? '', value: i.addressId })) || [],
      [otherOptions],
    )

    const {
      key: { PLACE_SEARCH_INPUT_BEHAVIOUR },
    } = useAppSetting()
    const { isError, isLoading, handlePlaceSearchOnEnterKeyPress, options, handlePlaceDetail } = usePlaceData({
      searchSessionToken,
      value: input,
      isEnabled: isCreatable,
      defaultOptions: defaultOptions,
      countryFallback,
      addressConfigs,
      isDebounced: PLACE_SEARCH_INPUT_BEHAVIOUR === 'debounce',
      isPressEnter: PLACE_SEARCH_INPUT_BEHAVIOUR === 'press_enter',
    })

    const handleCreateOption = (value: string) => {
      onSelectResult({ address: createAddress(value, countryFallback) })
    }

    const handleSelect = (value: DropdownItem<string | number | any>) => {
      if (value?.value) {
        const selected = otherOptions?.find((i) => i.addressId === value.value)

        if (selected) {
          onSelectResult({ address: { ...selected } })
          return
        }
      }

      if (value.data) {
        sendResultDetails(value.data)
      }
    }

    const sendResultDetails = async (rawPlace: Prediction) => {
      if (!addressConfigs) {
        Toast.toastMessage({ message: 'address_config_not_found', variant: 'error' })
        return
      }

      const res = await handlePlaceDetail({ rawPlace })

      if (res?.address) {
        onSelectResult({
          address: res.address,
          rawPlace,
        })
      }
    }

    const currentValue = useMemo(() => {
      if (!value) {
        return
      }

      const valuefromDefaultOptions = defaultOptions.find(
        (i) => i.value === value.addressId || i.label === value.formatted,
      )

      if (valuefromDefaultOptions) {
        return valuefromDefaultOptions
      }

      if (placeholder && !value.formatted) {
        return undefined
      }

      return {
        label: value.formatted || '',
        value: 'default',
      }
    }, [defaultOptions, placeholder, value])

    if (isError) {
      return <Text>something_went_wrong_error</Text>
    }

    const handleInput = (input: string) => {
      setInput(input)
    }

    return (
      <>
        <Flex gap={'0.3rem'} paddingX={'6px'}>
          <SynchronousAutocomplete
            {...props}
            cacheOptions={cacheOptions}
            options={options}
            ref={ref}
            placeholder={placeholder}
            defaultOptions={defaultOptions}
            isDisabled={props.isDisabled}
            isLoading={isLoading}
            onInputChange={PLACE_SEARCH_INPUT_BEHAVIOUR === 'debounce' ? handleInput : undefined}
            onChange={handleSelect}
            onCreateOption={handleCreateOption}
            value={currentValue}
            onKeyDown={PLACE_SEARCH_INPUT_BEHAVIOUR === 'press_enter' && handlePlaceSearchOnEnterKeyPress}
          />
          {PLACE_SEARCH_INPUT_BEHAVIOUR === 'press_enter' && isCreatable && placeInfoIconRight && (
            <Tooltip position="left" title="press_enter_to_search_lbl">
              <Icon icon={['far', 'circle-info']} color={colors.blue1} />
            </Tooltip>
          )}
        </Flex>
        {PLACE_SEARCH_INPUT_BEHAVIOUR === 'press_enter' && isCreatable && !placeInfoIconRight && (
          <Flex marginY={'0.5rem'} gap={'0.5rem'} paddingX={'6px'}>
            <Icon icon={['far', 'circle-info']} color={colors.blue1} />
            <Text fontSize={'14px'} lineHeight={'10px'}>
              press_enter_to_search_lbl
            </Text>
          </Flex>
        )}
      </>
    )
  },
)

export default SearchPlace
